import DownOutlined from '@ant-design/icons/DownOutlined';
import Popover from 'antd/es/popover';
import Typography from 'antd/es/typography';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';

import { useAccessibleTeams } from '../../../contexts/AccessibleTeamsContext';
import { useCurrentTeam } from '../../../contexts/CurrentTeamContext';
import { useSessionUser } from '../../../contexts/SessionUserContext';
import { useHealth } from '../../../queries/health';
import { useUserById } from '../../../queries/users';
import { TeamSwitcherPopover } from './TeamSwitcherPopover';

const { Text } = Typography;

const StyledTeamSwitcher = styled.div`
  /* Enables us to trigger the popover by clicking on the icon or padding */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: transparent;
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TeamSwitcher = () => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { teams, isLoading } = useAccessibleTeams();
  const { userId } = useSessionUser();
  const { currentTeamId } = useCurrentTeam();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const theme = useTheme();

  const userQuery = useUserById(userId);
  const healthQuery = useHealth();
  const currentTeam = teams.find((team) => team.teamId === teamId);

  const handleTeamSelect = (selectedTeamId: string) => {
    setIsPopoverOpen(false);
    void navigate(`/team/${selectedTeamId}`);
  };

  const handleSettingsClick = (selectedTeamId: string) => {
    setIsPopoverOpen(false);
    void navigate(`/team/${selectedTeamId}/settings`);
  };

  const handleProfileClick = () => {
    setIsPopoverOpen(false);
    void navigate('/profile');
  }

  const currentTeamName = currentTeam?.name || 'Select a team';
  const version = import.meta.env.VITE_VERSION || 'dev';

  const popoverContent = (
    <TeamSwitcherPopover
      teams={teams}
      currentTeamId={currentTeamId || undefined}
      user={userQuery.data}
      userId={userId}
      isLoading={isLoading}
      onTeamSelect={handleTeamSelect}
      onSettingsClick={handleSettingsClick}
      onProfileClick={handleProfileClick}
      version={version}
      backendVersion={healthQuery.data?.version}
    />
  );

  const popoverTrigger = (
    <StyledTeamSwitcher>
      <Text ellipsis>{currentTeamName}</Text>
      <DownOutlined
        style={{
          fontSize: theme.fontSizeSM,
          color: theme.colorTextTertiary,
          transform: isPopoverOpen ? 'rotate(-180deg)' : 'rotate(0deg)',
          transition: `transform ${theme.motionDurationMid} ease-in-out`,
        }}
      />
    </StyledTeamSwitcher>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      placement="bottom"
    >
      {popoverTrigger}
    </Popover>
  );
};
